import { FrondEngine } from "@frond/engine";
import { useEffect, useRef, useState } from "react";
import { loadActivePalette, pickImageFile } from "../state/tools";

interface Props {
  engine: FrondEngine;
}

/**
 * Canvas extras:
 * - right-click quick menu (InfiniPaint-style): palette colors, bookmark,
 *   view rotation
 * - inline text entry overlay (Text tool)
 * - image insertion (Image tool)
 * - keyboard shortcut "Tab" to hide the UI
 */
export function CanvasExtras({ engine }: Props) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [textPrompt, setTextPrompt] = useState<{
    cellId: string;
    x: number;
    y: number;
    screenX: number;
    screenY: number;
  } | null>(null);
  const [uiHidden, setUiHidden] = useState(false);
  const [textValue, setTextValue] = useState("");
  const textRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    document.body.classList.toggle("ui-hidden", uiHidden);
  }, [uiHidden]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        setUiHidden((h) => !h);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (textPrompt) {
      setTextValue("");
      // Focus after mount; select() is a no-op for empty value but harmless.
      requestAnimationFrame(() => {
        textRef.current?.focus();
      });
    }
  }, [textPrompt]);

  useEffect(() => {
    const offs = [
      engine.tools.on("request-text", (req) => {
        submittingRef.current = false;
        // eslint-disable-next-line no-console
        console.log("[frond:text] request", req);
        const p = engine.camera.cellToScreen(
          engine.renderer.size.w / 2,
          engine.renderer.size.h / 2,
          req.cellId,
        );
        const s = {
          x: p.a * req.x + p.c * req.y + p.e,
          y: p.b * req.x + p.d * req.y + p.f,
        };
        setTextPrompt({ ...req, screenX: s.x, screenY: s.y });
        requestAnimationFrame(() => textRef.current?.focus());
      }),
      engine.tools.on("request-image", (req) => {
        void pickImageFile().then((src) => {
          if (!src) return;
          const m = engine.camera.cellToScreen(
            engine.renderer.size.w / 2,
            engine.renderer.size.h / 2,
            req.cellId,
          );
          const k = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) || engine.camera.scale;
          const w = (200 / k) * 2;
          const h = (150 / k) * 2;
          if (engine.tools.addImage(req.cellId, req.x, req.y, w, h, src)) {
            engine.preloadImage(src);
            engine.requestRender();
          }
        });
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [engine]);

  useEffect(() => {
    const onContext = (e: MouseEvent) => {
      e.preventDefault();
      setMenu({ x: e.clientX, y: e.clientY });
    };
    const canvas = engineCanvas(engine);
    if (canvas) canvas.addEventListener("contextmenu", onContext);
    return () => canvas?.removeEventListener("contextmenu", onContext);
  }, [engine]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [menu]);

  const submitText = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      if (!textPrompt) return;
      const value = textValue;
      const prompt = textPrompt;
      // Debug: trace the full submit path (helps diagnose "input shows, no result").
      // eslint-disable-next-line no-console
      console.log("[frond:text] submit", {
        value,
        cellId: prompt.cellId,
        x: prompt.x,
        y: prompt.y,
        color: engine.tools.style.color,
        countBefore: engine.world.elementCount,
      });
      setTextPrompt(null);
      if (!value.trim()) {
        // eslint-disable-next-line no-console
        console.log("[frond:text] empty value, cancelled");
        return;
      }
      // The prompt cell may have vanished if the document was replaced
      // (gallery open, clear) while typing — recreate it so cellToScreen
      // never throws and the text always lands.
      try {
        engine.world.tree.ensure(prompt.cellId);
      } catch {
        /* MAX_DEPTH exceeded: fall through, cellToScreen will throw and be caught below */
      }
      const m = engine.camera.cellToScreen(
        engine.renderer.size.w / 2,
        engine.renderer.size.h / 2,
        prompt.cellId,
      );
      const det = m.a * m.d - m.b * m.c;
      const k =
        Number.isFinite(det) && Math.abs(det) > 1e-12
          ? Math.sqrt(Math.abs(det))
          : engine.camera.scale;
      const fontSize = Number.isFinite(k) && k > 1e-9 ? 24 / k : 24 / engine.camera.scale;
      if (!Number.isFinite(fontSize) || fontSize <= 0) {
        // eslint-disable-next-line no-console
        console.warn("[frond:text] invalid fontSize", { k, fontSize });
        return;
      }
      engine.tools.addText(prompt.cellId, prompt.x, prompt.y, value, fontSize, {
        color: engine.tools.style.color,
      });
      engine.requestRender();
      // eslint-disable-next-line no-console
      console.log("[frond:text] placed", {
        fontSize,
        countAfter: engine.world.elementCount,
      });
    } finally {
      submittingRef.current = false;
    }
  };

  const cancelText = () => {
    // Block the blur-submit that fires on unmount, then release for the next prompt.
    submittingRef.current = true;
    setTextPrompt(null);
    setTextValue("");
    // Reset on next tick so a subsequent request-text (which also resets)
    // always starts clean, even if no new prompt ever arrives.
    setTimeout(() => {
      submittingRef.current = false;
    }, 0);
  };

  const palette = loadActivePalette();

  return (
    <>
      {menu && (
        <div
          className="quick-menu"
          style={{ left: menu.x, top: menu.y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="quick-colors">
            {palette.map((c) => (
              <button
                key={c}
                className="swatch"
                style={{ background: c }}
                onClick={() => {
                  engine.tools.setColor(c);
                  setMenu(null);
                }}
              />
            ))}
          </div>
          <button
            className="btn block"
            onClick={() => {
              const pt = engine.camera.screenToFocus(
                engine.renderer.size.w / 2,
                engine.renderer.size.h / 2,
                { x: menu.x, y: menu.y },
              );
              engine.world.addBookmark(engine.camera.focusCellId, pt.x, pt.y, engine.camera.scale);
              setMenu(null);
            }}
          >
            🔖 Bookmark here
          </button>
          <button
            className="btn block"
            onClick={() => {
              engine.camera.rotation = 0;
              engine.requestRender();
              setMenu(null);
            }}
          >
            ⟲ Reset rotation
          </button>
        </div>
      )}

      {textPrompt && (
        <div
          className="text-overlay"
          style={{ left: textPrompt.screenX, top: textPrompt.screenY }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitText();
            }}
          >
            <input
              ref={textRef}
              autoFocus
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              style={{ color: engine.tools.style.color }}
              placeholder="Type… (Enter to place, Esc to cancel)"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                  cancelText();
                }
                // Enter is handled by form onSubmit (covers IME, numpad, mobile).
              }}
              onBlur={submitText}
            />
          </form>
        </div>
      )}
    </>
  );
}

function engineCanvas(engine: FrondEngine): HTMLCanvasElement | null {
  // The engine canvas is the one inside the app's canvas host.
  return document.querySelector<HTMLCanvasElement>(".canvas-host canvas");
}
